import { useRef, useEffect, useState } from 'react';
import PalmerSectionLabel from './PalmerSectionLabel';

const works = [
  {
    id: '01',
    title: 'Sonder Goods',
    tag: 'Brand Identity',
    mainImg: 'https://framerusercontent.com/images/wA52DtSvQDx894hqLZv4ezfKfz8.png',
    hoverImg: 'https://framerusercontent.com/images/WSIwyrpSzX4O0fiESBwPTjSWBE.png',
  },
  {
    id: '02',
    title: 'Halo Wear',
    tag: 'Web Design',
    mainImg: 'https://framerusercontent.com/images/IhwR33YbJAKylGnbmoCW4maBHI.png',
    hoverImg: 'https://framerusercontent.com/images/tkYEeCoj1udozbnzQynoaYqCI.png',
  },
  {
    id: '03',
    title: 'Lucent Lab',
    tag: 'Motion',
    mainImg: 'https://framerusercontent.com/images/G891sPJdh93gPfGSBboEt88Now.png',
    hoverImg: 'https://framerusercontent.com/images/YIi7jRxIe8p6gLtM1ZMNpJyVYs.jpeg',
  },
  {
    id: '04',
    title: 'Arc & Bloom',
    tag: 'Development',
    mainImg: 'https://framerusercontent.com/images/kSBqNFitJQuBzXuk7tl1FqlAHhs.png',
    hoverImg: 'https://framerusercontent.com/images/Jt7zqgTjQMYT15YvEkLGKiF9Cw.png',
  },
  {
    id: '05',
    title: 'Atelier Nara',
    tag: 'AI Strategy',
    mainImg: 'https://framerusercontent.com/images/svmMd86RbsKfib7KzvpKAUsHrk.png',
    hoverImg: 'https://framerusercontent.com/images/7WVAcnCw5jrTdcET3CmMrpU7gf0.png',
  },
];

function WorkCard({ work, delay = 0 }: { work: typeof works[0]; delay?: number }) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden cursor-pointer group transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${delay}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image container */}
      <div className="relative overflow-hidden w-full" style={{ aspectRatio: '4/5' }}>
        <img
          src={work.mainImg}
          alt={work.title}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        {/* Hover overlay */}
        <div
          className="absolute inset-0 transition-opacity duration-500"
          style={{ opacity: hovered ? 1 : 0 }}
        >
          <img
            src={work.hoverImg}
            alt={work.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
        {/* Gradient on hover */}
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent transition-opacity duration-300"
          style={{ opacity: hovered ? 1 : 0 }}
        />
        {/* Tag badge */}
        <div
          className="absolute top-3 left-3 border border-white/20 px-2 py-1 transition-opacity duration-300"
          style={{ opacity: hovered ? 1 : 0 }}
        >
          <span className="text-white text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>{work.tag}</span>
        </div>
      </div>
      {/* Title row */}
      <div className="mt-3 flex items-center justify-between">
        <span className="text-white text-sm font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          {work.title}
        </span>
        <span className="text-white/30 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          ({work.id})
        </span>
      </div>
    </div>
  );
}

export default function PalmerWork() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-black py-20">
      <PalmerSectionLabel
        left1="© Featured Projects プロジェクト"
        left2="(WDX® — 03)"
        right="Creative Development"
      />

      <div className="px-6">
        {/* Section heading */}
        <div className="overflow-hidden mb-0">
          <h2
            className={`text-white font-semibold transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 'clamp(48px, 7.5vw, 110px)',
              letterSpacing: '-0.04em',
              lineHeight: 1,
            }}
          >
            Featured Works©
          </h2>
        </div>

        <div className="h-px bg-white/10 my-6"></div>

        {/* Description + CTA */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-10">
          <p
            className="text-white/40 text-sm leading-relaxed max-w-lg"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Every project is a chance to blend design and development, shaping bold interactive ideas into{' '}
            <strong className="text-white/60">sleek digital realities — built with</strong> intent, speed, and visual clarity.
          </p>
          <a
            href="/work"
            className="inline-flex items-center border border-white/30 text-white text-xs px-5 py-2.5 hover:bg-white hover:text-black transition-all duration-300 whitespace-nowrap self-start"
            style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.1em' }}
          >
            SEE WORKS
          </a>
        </div>

        {/* Work grid — top row: 2 cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <WorkCard work={works[0]} delay={0} />
          <WorkCard work={works[1]} delay={100} />
        </div>
        {/* Bottom row: 3 cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <WorkCard work={works[2]} delay={0} />
          <WorkCard work={works[3]} delay={100} />
          <WorkCard work={works[4]} delay={200} />
        </div>
      </div>
    </section>
  );
}
